import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar/AppBar';
import Button from '@mui/material/Button/Button';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Toolbar from '@mui/material/Toolbar/Toolbar';
import Typography from '@mui/material/Typography/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import usePreviewIdentity from '../../../hooks/usePreviewIdentity';

const DEFAULT_PREVIEW_TITLE = 'Course Preview';
const PREVIEW_COURSE_TITLE_EVENT = 'preview-course-title';

export default function PreviewShareNavBar() {
  const theme = useTheme();
  const { isAuthenticated, user } = useAuth0();
  const [previewTitle, setPreviewTitle] = useState(DEFAULT_PREVIEW_TITLE);
  const [identityMenuAnchor, setIdentityMenuAnchor] = useState<HTMLElement | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { hasStoredIdentity, identityLabel, clearIdentity } = usePreviewIdentity();
  const authenticatedIdentityLabel = user?.name?.trim() || user?.email?.trim() || '';
  const showIdentityLabel = isAuthenticated ? authenticatedIdentityLabel !== '' || hasStoredIdentity : hasStoredIdentity;
  const resolvedIdentityLabel = isAuthenticated ? authenticatedIdentityLabel || identityLabel : identityLabel;
  const homeBrand = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <img src={'/logo.png'} alt='' width={30} height={30} style={{ marginRight: 10 }} />
      <Typography variant='h6' noWrap color='inherit' fontStyle={'italic'}>
        Mentorkit
      </Typography>
    </div>
  );

  useEffect(() => {
    const handleCourseTitleUpdated = (event: Event) => {
      const courseTitle = ((event as CustomEvent<string>).detail || '').trim();
      setPreviewTitle(courseTitle || DEFAULT_PREVIEW_TITLE);
    };

    window.addEventListener(PREVIEW_COURSE_TITLE_EVENT, handleCourseTitleUpdated);
    return () => {
      window.removeEventListener(PREVIEW_COURSE_TITLE_EVENT, handleCourseTitleUpdated);
    };
  }, []);

  const handleIdentityMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIdentityMenuAnchor(event.currentTarget);
  };

  const handleIdentityMenuClose = () => {
    setIdentityMenuAnchor(null);
  };

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    clearIdentity();
    handleIdentityMenuClose();
    setIsLoggingOut(false);
  };

  return (
    <AppBar
      position='sticky'
      sx={{
        flex: '0 1 auto',
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.bgZero.contrastText,
        background: theme.palette.bgZero.main,
        boxShadow: 'none',
      }}
      >
      <Toolbar variant='dense' sx={{ justifyContent: 'space-between', gap: 2 }}>
        {isAuthenticated ? (
          <Link to={'/'} style={{ color: 'inherit', textDecoration: 'none' }}>
            {homeBrand}
          </Link>
        ) : (
          <Box
            aria-disabled='true'
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              cursor: 'not-allowed',
              pointerEvents: 'none',
            }}
          >
            {homeBrand}
          </Box>
        )}

        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
            maxWidth: { xs: 180, sm: 360, md: 520 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
          title={previewTitle}
        >
          {previewTitle}
        </Typography>

        {showIdentityLabel ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 125 }}>
            {isAuthenticated ? (
              <Typography
                variant='body2'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 36,
                  maxWidth: 220,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  fontWeight: 500,
                }}
                title={resolvedIdentityLabel}
              >
                {resolvedIdentityLabel}
              </Typography>
            ) : (
              <>
                <Button
                  color='inherit'
                  onClick={handleIdentityMenuOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    textTransform: 'none',
                    maxWidth: 220,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {resolvedIdentityLabel}
                </Button>
                <Menu anchorEl={identityMenuAnchor} open={Boolean(identityMenuAnchor)} onClose={handleIdentityMenuClose}>
                  <MenuItem onClick={() => void handleLogout()} disabled={isLoggingOut}>
                    {isLoggingOut ? 'Logging out...' : 'Log out'}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ minWidth: 125 }} />
        )}
      </Toolbar>
    </AppBar>
  );
}
